import * as THREE from 'three'
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js'
import { FontLoader } from 'three/addons/loaders/FontLoader.js';
import { gsap } from "gsap";
import GoldSweep from '../particles/GoldSweep';   // ← replaces old Particles import
import PerfumeBottle from '../threeMeshes/bottlemesh';

export default class ThreeController {

    constructor() {
        this.clock        = new THREE.Clock();
        this.threeElements = [];
        this.init();
    }

    init() {
        this._load();
        this._setupScene();
        this.perfumeBottle = new PerfumeBottle(this.scene);
    }

    // ── Particles (GoldSweep) ────────────────────────────────────────────
    createParticles() {
        // GoldSweep creates its own canvas overlay — nothing added to the scene
        this.particles = new GoldSweep();
        // models is [] so the forEach in script.js is a safe no-op
        // Don't push to threeElements — GoldSweep.tick() is a no-op and
        // manages its own RAF loop internally
    }

    // Called by script.js navigateToSection — same interface as before
    smokeAnimation() {
        if (this.particles) {
            this.particles.smokeAnimation();
        }
    }

    // ── Bottle ───────────────────────────────────────────────────────────
    async addPerfumeBottle(perfumeImage, perfumeName) {
        await document.fonts.load('10pt "Tangerine"');
        if (!this.perfumeBottle) {
            this.perfumeBottle = new PerfumeBottle(this.scene);
        }
        this.perfumeBottle.addBottle(perfumeImage, perfumeName);
    }

    removeCurrentPerfumeBottle() {
        if (this.perfumeBottle) {
            this.perfumeBottle.removeBottle();
        }
    }

    // ── Loading manager ──────────────────────────────────────────────────
    _load() {
        const loadingBarElement = document.querySelector('.loading-bar');

        const loadingManager = new THREE.LoadingManager(
            () => {
                gsap.to('.loading-bar', {
                    width: '100%', duration: 0.5, ease: "power2.inOut",
                    onComplete: () => {
                        gsap.to('#loading-screen', {
                            opacity: 0, duration: 0.5, ease: "power2.inOut",
                            onComplete: () => {
                                document.getElementById('loading-screen').style.display = 'none';
                            }
                        });
                    }
                });
            },
            (itemUrl, itemsLoaded, itemsTotal) => {
                const pct = (itemsLoaded / itemsTotal) * 100;
                loadingBarElement.style.width = pct + '%';
                document.getElementById('loading-percentage').innerText = Math.round(pct) + '%';
            }
        );

        this.loadingManager = loadingManager;
    }


    // ── NEW: manually complete loading screen ────────────────────────────
    _trackPageAssets() {
        const bar     = document.querySelector('.loading-bar');
        const pctText = document.getElementById('loading-percentage');
    
        // All images currently in the DOM
        const allImages    = Array.from(document.images);
        const total        = allImages.length;
    
        // Some may already be cached and complete
        let loaded = allImages.filter(img => img.complete).length;
    
        const update = () => {
            loaded++;
            const pct = Math.min(100, Math.round((loaded / total) * 100));
            bar.style.width   = pct + '%';
            if (pctText) pctText.innerText = pct + '%';
            if (loaded >= total) {
                setTimeout(() => this._completeLoadingScreen(), 400);
            }
        };
    
        // Nothing to track — complete gracefully
        if (total === 0 || loaded >= total) {
            bar.style.width = '100%';
            if (pctText) pctText.innerText = '100%';
            setTimeout(() => this._completeLoadingScreen(), 400);
            return;
        }
    
        // Listen to images still in flight
        allImages.forEach(img => {
            if (!img.complete) {
                img.addEventListener('load',  update, { once: true });
                img.addEventListener('error', update, { once: true }); // errors count too — never get stuck
            }
        });
    
        // Safety net — never leave user stuck longer than 10 seconds
        setTimeout(() => this._completeLoadingScreen(), 10000);
    }
    
    _completeLoadingScreen() {
        const screen = document.getElementById('loading-screen');
        const bar    = document.querySelector('.loading-bar');
        if (!screen || screen.style.display === 'none') return;
    
        gsap.to(bar, {
            width: '100%', duration: 0.4, ease: "power2.inOut",
            onComplete: () => {
                gsap.to(screen, {
                    opacity: 0, duration: 0.7, ease: "power2.inOut",
                    onComplete: () => { screen.style.display = 'none'; }
                });
            }
        });
    }
    // ── Scene setup ──────────────────────────────────────────────────────
    _setupScene() {
        const sizes = { width: window.innerWidth, height: window.innerHeight };

        this.canvas       = document.querySelector('canvas.webgl');
        this.scene        = new THREE.Scene();
        this.textureLoader = new THREE.TextureLoader();
        this.fontLoader   = new FontLoader();
        this.textureLoader.crossOrigin = '';

        this.renderer = new THREE.WebGLRenderer({ canvas: this.canvas, antialias: true });
        this.camera   = new THREE.PerspectiveCamera(75, sizes.width / sizes.height, 0.1, 100);
        this.scene.add(this.camera);

        this._configureCamera();
        this._configureRenderer(sizes);
        this._setControls();
        this._setupLights();
        this.tick();

        
        // ✅ new
        this._trackPageAssets();
    }

    _setupLights() {
        const dirLight = new THREE.DirectionalLight('#ffffff', 3);
        dirLight.castShadow = false;
        dirLight.position.set(0.25, 3, -2.25);
        this.scene.add(dirLight);

        const pointLight = new THREE.PointLight('#f5c42b', 3);
        pointLight.castShadow = false;
        pointLight.position.set(0.25, 3, -2.25);
        this.scene.add(pointLight);
    }

    _setControls() {
        // OrbitControls disabled — prevents accidental scene rotation
        // and saves the damping RAF overhead
        const controls = new OrbitControls(this.camera, this.canvas);
        controls.enabled       = false;
        controls.enableRotate  = false;
        controls.enableZoom    = false;
        controls.enablePan     = false;
    }

    _configureRenderer(sizes) {
        this.renderer.setClearColor(0x000000, 0);
        this.renderer.toneMapping         = THREE.ACESFilmicToneMapping;
        this.renderer.toneMappingExposure = 0.55;
        this.renderer.shadowMap.enabled   = false; // disabled — bottle is decorative, saves GPU
        this.renderer.setSize(sizes.width, sizes.height);
        const isMob = window.innerWidth < 768;
        this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, isMob ? 1.5 : 2));
    }

    _configureCamera() {
        this.camera.position.set(0, 2, 5);
        this.camera.near = 0.1;
        this.camera.far  = 100;
        this.camera.updateProjectionMatrix();
    }

    // ── Tick ─────────────────────────────────────────────────────────────
    tickElements(time) {
        this.threeElements.forEach(el => el.tick(time));
    }

    tick() {
        const time = Date.now() * 0.0005;
        this.tickElements(time);

        // Bottle rotation is handled inside PerfumeBottle.tick()
        if (this.perfumeBottle) this.perfumeBottle.tick(time);

        this.renderer.render(this.scene, this.camera);
        requestAnimationFrame(this.tick.bind(this));
    }
}